/* eslint-disable jsx-a11y/no-redundant-roles */
import React, { createContext } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { YouTube } from 'mdx-embed';
import Img from 'gatsby-image';
import { paramCase } from 'change-case';
import jstz from 'jstz';
import { format as zonedFormat, toDate } from 'date-fns-tz';
import enUS from 'date-fns/locale/en-US';

export const DataContext = createContext(null);
export const { Provider: DataProvider, Consumer: DataConsumer } = DataContext;

export default function PostShowArchive(section) {
  let {
    conference: { days, speakers: cmsSpeakers },
    slots,
    speakers,
    sessions,
  } = useQuery();

  speakers = speakers.nodes.map(({ id, data }) => ({ id, ...data }));
  sessions = sessions.nodes.map(({ id, data }) => ({ id, ...data }));

  const timeZone = jstz.determine().name();

  slots = slots.nodes.reduce((arr, { id, data }) => {
    if (!~['Talk', 'Lightning Talk'].indexOf(data.format)) return arr; // get ONLY talk slots
    data.startTime = toDate(data.startTime);
    const format = (str) =>
      zonedFormat(data.startTime, str, {
        local: enUS,
        timeZone,
      });
    data.startWeekDay = format('EEEE');
    data.startDay = format('d');
    data.startDayTh = format('do');
    data.startMonth = format('MMMM');
    data.startYear = format('yyyy');
    data.startHour = format('HH:mm');
    data.startZone = format('zz');

    if (data.speakers !== null) {
      data.speakers = data.speakers.map((id) =>
        speakers.find((speaker) => speaker.id === id)
      );
    }
    if (data.sessions !== null) {
      data.sessions = data.sessions.map((id) =>
        sessions.find((session) => session.id === id)
      );
    }
    arr.push({ id, ...data });
    return arr;
  }, []);

  slots.sort((a, b) => {
    return new Date(a.startTime) > new Date(b.startTime) ? 1 : -1;
  });

  function findCmsSpeaker(name) {
    return cmsSpeakers.find(
      (cs) => paramCase(cs.name.trim()) === paramCase(name.trim())
    );
  }

  return (
    <section className="bg-haiti">
      <div className="frame wrap-l stack-xl py-xl">
        <div className="stack-m">
          <h2 id="talks" className="f-sans-xl a-center">
            {section.heading}
          </h2>
          {section.content.length > 0 && (
            <div
              className="prose-sans"
              dangerouslySetInnerHTML={{
                __html: section.contentNode.childMarkdownRemark.html,
              }}
              style={{ '--font-size': '24px' }}
            ></div>
          )}
        </div>
        <ol role="list" className="stack-xl">
          {slots.map((slot) => {
            const { startWeekDay, startDayTh, startMonth } = slot;
            const session = slot.sessions && slot.sessions[0];
            console.table(session && session.youtube);
            return (
              session &&
              session.youtube && (
                <li role="listitem" key={slot.startTime}>
                  <article className="grid-m m__cols-2">
                    {/* texts */}
                    <div className="m__span-2 stack-m">
                      <p className="f-mono-xs fg-ui-secondary">
                        {startWeekDay} {startDayTh} {startMonth}{' '}
                        {slot.startHour} {slot.startZone}
                      </p>
                      <h3 className="fg-scheme f-sans-l">{session.title}</h3>
                      {session.descr && (
                        <p className="f-sans-s">{session.descr}</p>
                      )}
                      {session.slides && (
                        <p className="f-mono-xs fg-ui-secondary">
                          <a
                            href={session.slides}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Link to slides
                          </a>
                        </p>
                      )}
                    </div>
                    {/* video */}
                    <YouTube youTubeId={session.youtube} />
                    {/* speakers */}
                    <ul role="list">
                      {slot.speakers.map((speaker) => {
                        const cmsSpeaker = findCmsSpeaker(speaker.name);
                        return (
                          <li
                            role="listitem"
                            className="stop"
                            key={speaker.name}
                          >
                            <div
                              className="flow-grid"
                              style={{ '--flow-x-gap': '20px' }}
                            >
                              <div
                                className="cell span-cell"
                                style={{ '--cell-width': '80px' }}
                              >
                                <Img fixed={cmsSpeaker.image.fixed} />
                              </div>
                              <div className="cell span-auto">
                                <div
                                  className="stack"
                                  style={{ '--stack-gap': '16px' }}
                                >
                                  <div className="f-sans-s fw-500">
                                    {speaker.name}
                                  </div>
                                  <div className="f-sans-xs">
                                    {speaker.title.trim()}
                                    {speaker.company &&
                                      `, ${speaker.company.trim()}`}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </article>
                </li>
              )
            );
          })}
        </ol>
      </div>
    </section>
  );
}

function useQuery() {
  return useStaticQuery(graphql`
    {
      slots: allAirtable(filter: { table: { eq: "Agenda" } }) {
        nodes {
          id: recordId
          data {
            startTime: Start_Time
            length: Length
            day: Day
            speakers: Speaker_Link
            sessions: Session_Link
            topics: Topic
            format: Format
          }
        }
      }
      speakers: allAirtable(filter: { table: { eq: "Speakers" } }) {
        nodes {
          id: recordId
          data {
            name: Name
            title: Title
            company: Company
            slots: Agenda_Link
            sessions: Session_Link
          }
        }
      }
      sessions: allAirtable(filter: { table: { eq: "Sessions" } }) {
        nodes {
          id: recordId
          data {
            title: Title
            descr: Description
            speakers: Speaker_Link
            youtube: Youtube_ID
            slides: Slides_URL
          }
        }
      }
      conference: datoCmsConference(title: { eq: "Rasa Summit 2021" }) {
        title
        days {
          topic
          startTime
        }
        speakers {
          personType
          links {
            ... on DatoCmsFacebook {
              username
            }
            ... on DatoCmsTwitter {
              username
            }
            ... on DatoCmsLinkedin {
              username
            }
          }
          name
          role
          company
          dept
          bioNode {
            childMarkdownRemark {
              html
            }
          }
          image {
            fixed(
              width: 80
              height: 80
              imgixParams: {
                fm: "jpg"
                auto: "compress"
                fit: "crop"
                crop: "faces"
              }
            ) {
              ...GatsbyDatoCmsFixed
            }
          }
        }
      }
    }
  `);
}
